import {
	createGlobalEmitter,
	type GlobalEmitter,
} from "@solid-primitives/event-bus";
import { createContext, type ParentProps, useContext } from "solid-js";
import { unwrap } from "solid-js/store";
import type {
	MessageListAnchor,
	MessageRange,
} from "@/api/services/MessagesService";
import { useChannel } from "@/contexts/mod";
import type { ChannelT } from "@/types";
import type { TimelineItemT2 } from "./util";

export type TimelineController = {
	jumpToBottom(smooth?: boolean): void;
	jumpToTop(smooth?: boolean): void;
	jumpToMessage(
		message_id: string,
		smooth?: boolean,
		highlight?: boolean,
	): void;
	scrollBy(px: number, smooth?: boolean): void;
	ackMessage(message_id: string): void;

	events: GlobalEmitter<TimelineEvents>;
	commands: GlobalEmitter<TimelineCommands>;
};

export type TimelineEvents = {
	/** scroll position updated */
	scrollPosition: number;

	/** scroll index changed
	 *
	 * passes the index of the timeline item that the user is scrolled to */
	scrollIndex: number;

	/** scrolled to top of channel */
	scrollTop: void;

	/** scrolled to bottom of channel */
	scrollBottom: void;

	/** pagination triggered */
	paginate: MessageListAnchor;
};

export type TimelineCommands = {
	scrollBy: { px: number; smooth: boolean };
	jumpToBottom: { smooth: boolean };
	jumpToTop: { smooth: boolean };
	jumpToMessage: { message_id: string; smooth: boolean; highlight: boolean };
	ackMessage: { message_id: string };
};

export type TimelineState = {
	messages: MessageRange | null;
	anchor: MessageListAnchor;
	loading: boolean; // NOTE: unnecessary with queue system?
	highlight: string | null;
	scrollTop: number;
	items: TimelineItemT2[];
	readMarkerId: string | null;

	controller: TimelineController;
	events: GlobalEmitter<TimelineEvents>;
	commands: GlobalEmitter<TimelineCommands>;
};

export const TimelineContext = createContext<TimelineState>();

export type TimelineProviderProps = ParentProps & {
	channel: ChannelT;
};

export const TimelineProvider = (props: TimelineProviderProps) => {
	const [chanState] = useChannel();
	const state = unwrap(chanState.timelineState);

	// Initialize channel-specific anchor on first render (before any fetch)
	if (state.messages === null) {
		const readMarker = props.channel.last_read_id;
		const hasReadMarker =
			readMarker && readMarker !== props.channel.last_version_id;
		if (hasReadMarker) {
			state.anchor = { type: "context", limit: 50, message_id: readMarker };
		}
		state.readMarkerId = readMarker ?? null;
	}

	return (
		<TimelineContext.Provider value={state}>
			{props.children}
		</TimelineContext.Provider>
	);
};

export const useTimeline = (): TimelineState => {
	const ctx = useContext(TimelineContext);
	if (!ctx) {
		throw new Error(
			"useTimeline must be used within a TimelineContext.Provider",
		);
	}
	return ctx;
};

export const createTimelineController = (): TimelineController => {
	const events = createGlobalEmitter<TimelineEvents>();
	const commands = createGlobalEmitter<TimelineCommands>();

	return {
		jumpToBottom(smooth = false) {
			commands.emit("jumpToBottom", { smooth });
		},
		jumpToTop(smooth = false) {
			commands.emit("jumpToTop", { smooth });
		},
		jumpToMessage(message_id, smooth = false, highlight = false) {
			commands.emit("jumpToMessage", { message_id, smooth, highlight });
		},
		scrollBy(px: number, smooth = false) {
			commands.emit("scrollBy", { px, smooth });
		},
		ackMessage(message_id: string) {
			commands.emit("ackMessage", { message_id });
		},

		events,
		commands,
	};
};
